import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, IconButton, ToggleButton, ToggleButtonGroup,
  CircularProgress, Chip, Grid, Divider
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { useBranch } from '../context/BranchContext';
import {
  getMonday, addDays, getWeekDays, getTodayISO, formatDateISO,
  getWeeksInMonth, DAY_KEYS, buildVacationDateSet
} from '../utils/dateUtils';

const DAY_ABBREV = (lang) => {
  const map = {
    en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    es: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
  };
  return map[lang === 'spanish' ? 'es' : 'en'];
};

function DayCard({ dayData, dateStr, lang, isToday, isCurrentMonth, vacationSet, allBranches }) {
  const { t } = useTranslation();
  const active = dayData && dayData.active !== false;
  const isOff = !active;
  const isVacation = vacationSet && vacationSet.has(dateStr);

  const branchName = React.useMemo(() => {
    const branchId = dayData && dayData.branch;
    if (!branchId) return '';
    const b = allBranches.find((br) => (br.branch_id || br.code) === branchId);
    return b ? b.name : branchId;
  }, [dayData, allBranches]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minHeight: 80,
        bgcolor: isToday ? 'rgba(25, 118, 210, 0.12)' : isVacation ? 'rgba(76, 175, 80, 0.08)' : isOff ? 'grey.100' : 'rgba(25, 118, 210, 0.06)',
        border: isToday ? '2px solid' : '1px solid',
        borderColor: isToday ? 'primary.main' : isVacation ? 'success.light' : isOff ? 'grey.300' : 'primary.light',
        opacity: isCurrentMonth === false ? 0.4 : 1,
        transition: 'all 0.15s',
        borderRadius: 2,
        '&:hover': { boxShadow: 1 }
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: isToday ? 'primary.main' : isVacation ? 'success.dark' : isOff ? 'grey.600' : 'text.secondary' }}>
        {dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString(lang === 'spanish' ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' }) : ''}
      </Typography>
      {isVacation ? (
        <Chip label={t('calendar_schedule_vacation')} size="small" color="success" variant="outlined" sx={{ mt: 0.5, fontSize: '0.7rem', fontWeight: 500 }} />
      ) : isOff ? (
        <Chip label={t('calendar_schedule_off')} size="small" sx={{ mt: 0.5, bgcolor: 'grey.200', color: 'grey.600', fontSize: '0.7rem', fontWeight: 500 }} />
      ) : (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'primary.main' }}>
            {dayData.start} – {dayData.end}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              if (!dayData.start || !dayData.end) return '';
              const [sh, sm] = dayData.start.split(':').map(Number);
              const [eh, em] = dayData.end.split(':').map(Number);
              const hrs = Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
              return hrs > 0 ? `${hrs.toFixed(1)} hrs` : '';
            })()}
          </Typography>
          {branchName && (
            <Chip label={branchName} size="small" sx={{ mt: 0.5, fontSize: '0.65rem', fontWeight: 500, height: 20 }} />
          )}
        </Box>
      )}
    </Paper>
  );
}

function MonthCell({ dayData, dateStr, isToday, vacationSet, allBranches }) {
  const { t } = useTranslation();
  const active = dayData && dayData.active !== false;
  const isOff = !active;
  const isVacation = vacationSet && vacationSet.has(dateStr);

  const branchName = React.useMemo(() => {
    const branchId = dayData && dayData.branch;
    if (!branchId) return '';
    const b = allBranches.find((br) => (br.branch_id || br.code) === branchId);
    return b ? b.name : branchId;
  }, [dayData, allBranches]);

  return (
    <Box
      sx={{
        p: 0.5,
        minHeight: 64,
        bgcolor: isToday ? 'rgba(25, 118, 210, 0.12)' : isVacation ? 'rgba(76, 175, 80, 0.08)' : active ? 'rgba(25, 118, 210, 0.06)' : 'grey.100',
        border: '1px solid',
        borderColor: isToday ? 'primary.main' : isVacation ? 'success.light' : active ? 'primary.light' : 'grey.300',
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: isToday ? 'primary.main' : isVacation ? 'success.dark' : isOff ? 'grey.600' : 'text.primary' }}
      >
        {new Date(dateStr + 'T00:00:00').getDate()}
      </Typography>
      <Typography variant="caption" display="block" sx={{ lineHeight: 1.3, mt: 0.25 }}>
          {isVacation ? (
            <span style={{ color: '#2e7d32', fontWeight: 500 }}>{t('calendar_schedule_vacation')}</span>
          ) : active ? (
            <>
              <span style={{ color: '#1565c0', fontWeight: 500 }}>{dayData.start}–{dayData.end}</span>
              {branchName && (
                <span style={{ color: '#1565c0', fontWeight: 500, display: 'block', fontSize: '0.65rem' }}>{branchName}</span>
              )}
            </>
          ) : (
            <span style={{ color: '#757575', fontWeight: 500 }}>{t('calendar_schedule_off')}</span>
          )}
        </Typography>
    </Box>
  );
}

export default function EmployeeScheduleCalendar({ userEmail, vacations }) {
  const { t, lang } = useTranslation();
  const { branches: allBranches } = useBranch();
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(getTodayISO());
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthSchedules, setMonthSchedules] = useState({});
  const [prevMonthSchedules, setPrevMonthSchedules] = useState({});
  const [monthLoading, setMonthLoading] = useState(false);

  const today = getTodayISO();
  const currentMonday = getMonday(currentDate);

  // Fetch schedule for week view
  useEffect(() => {
    if (!userEmail || viewMode !== 'week') return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_ENDPOINTS.SCHEDULE_EFFECTIVE(userEmail)}?week_start=${currentMonday}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        setSchedule(data?.schedule || null);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) { setSchedule(null); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [userEmail, currentMonday, viewMode]);

  // Fetch schedules for all weeks in month view
  useEffect(() => {
    if (viewMode !== 'month' || !userEmail) return;
    
    let cancelled = false;
    setPrevMonthSchedules({ ...monthSchedules });
    setMonthLoading(true);
    
    const monthDate = new Date(currentDate + 'T00:00:00');
    const monthYear = monthDate.getFullYear();
    const monthNum = monthDate.getMonth();
    const weeks = getWeeksInMonth(monthYear, monthNum);
    
    Promise.all(
      weeks.map(weekMonday =>
        fetch(`${API_ENDPOINTS.SCHEDULE_EFFECTIVE(userEmail)}?week_start=${weekMonday}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(data => ({ weekMonday, schedule: data?.schedule || null }))
          .catch(() => ({ weekMonday, schedule: null }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ weekMonday, schedule }) => {
        map[weekMonday] = schedule;
      });
      setPrevMonthSchedules(map);
      setMonthSchedules(map);
      setMonthLoading(false);
    });
    
    return () => { cancelled = true; };
  }, [viewMode, userEmail, currentDate]);

  const scrollPosRef = React.useRef(0);

  React.useEffect(() => {
    const isLoading = (viewMode === 'week' && loading) || (viewMode === 'month' && monthLoading);
    if (!isLoading && scrollPosRef.current > 0) {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosRef.current);
        scrollPosRef.current = 0;
      });
    }
  }, [loading, monthLoading, viewMode]);

  const saveScrollAnd = (fn) => {
    scrollPosRef.current = window.scrollY;
    fn();
  };

  const navigatePrev = () => saveScrollAnd(() => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(getMonday(currentDate), -7));
    } else {
      const d = new Date(currentDate + 'T00:00:00');
      d.setMonth(d.getMonth() - 1);
      setCurrentDate(formatDateISO(d));
    }
  });

  const navigateNext = () => saveScrollAnd(() => {
    if (viewMode === 'week') {
      setCurrentDate(addDays(getMonday(currentDate), 7));
    } else {
      const d = new Date(currentDate + 'T00:00:00');
      d.setMonth(d.getMonth() + 1);
      setCurrentDate(formatDateISO(d));
    }
  });

  const goToToday = () => saveScrollAnd(() => setCurrentDate(getTodayISO()));

  const weekDays = getWeekDays(currentMonday);

  const monthDate = new Date(currentDate + 'T00:00:00');
  const monthYear = monthDate.getFullYear();
  const monthNum = monthDate.getMonth();
  const weeksInMonth = getWeeksInMonth(monthYear, monthNum);

  const abbrevs = DAY_ABBREV(lang);
  const locale = lang === 'spanish' ? 'es-MX' : 'en-US';
  const monthLabel = monthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  const weekStartDate = new Date(currentMonday + 'T00:00:00');
  const weekEndDate = new Date(addDays(currentMonday, 6) + 'T00:00:00');
  const weekOpts = { month: 'short', day: 'numeric' };
  const weekRangeBase = `${weekStartDate.toLocaleDateString(locale, weekOpts)} – ${weekEndDate.toLocaleDateString(locale, weekOpts)}`;
  const weekRangeLabel = weekStartDate.getFullYear() !== weekEndDate.getFullYear()
    ? `${weekStartDate.toLocaleDateString(locale, { ...weekOpts, year: 'numeric' })} – ${weekEndDate.toLocaleDateString(locale, { ...weekOpts, year: 'numeric' })}`
    : `${weekRangeBase}, ${weekStartDate.getFullYear()}`;

  const vacationSet = React.useMemo(() => buildVacationDateSet(vacations), [vacations]);
  const hasSchedule = viewMode === 'week' ? Boolean(schedule?.days) : !monthLoading;
  const isWeekLoading = loading;
  const isMonthLoading = monthLoading;

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
        >
          <ToggleButton value="week" sx={{ textTransform: 'none' }}>{t('calendar_view_week')}</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: 'none' }}>{t('calendar_view_month')}</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={navigatePrev} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={goToToday} size="small" title={t('calendar_nav_thisweek')}>
            <TodayIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: { xs: 110, sm: 160 }, textAlign: 'center', fontWeight: 600 }}>
            {viewMode === 'week' ? weekRangeLabel : monthLabel}
          </Typography>
          <IconButton onClick={navigateNext} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {viewMode === 'week' && isWeekLoading ? (
        <Box sx={{ textAlign: 'center', p: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('calendar_schedule_loading')}
          </Typography>
        </Box>
      ) : viewMode === 'week' && !hasSchedule ? (
        <Box sx={{ textAlign: 'center', p: 6 }}>
          <Typography variant="h6" color="text.secondary">{t('calendar_schedule_empty')}</Typography>
        </Box>
      ) : viewMode === 'week' ? (
        /* Week View */
        <Box>
          <Grid container spacing={1.5}>
            {weekDays.map((dateStr, idx) => {
              const dayKey = DAY_KEYS[idx];
              const dayData = schedule?.days?.[dayKey];
              return (
                <Grid item xs={12} sm={6} md key={dateStr}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                      {abbrevs[idx]}
                    </Typography>
                    <DayCard
                      dayData={dayData}
                      dateStr={dateStr}
                      lang={lang}
                      isToday={dateStr === today}
                      vacationSet={vacationSet}
                      allBranches={allBranches}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          {schedule?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('calendar_schedule_name_label', { name: schedule.name })}
            </Typography>
          )}
        </Box>
      ) : (
        /* Month View */
        <Box sx={{ position: 'relative' }}>
          {isMonthLoading && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* Day-of-week header */}
          <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
            {abbrevs.map((label) => (
              <Grid item xs key={label}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', textAlign: 'center', py: 0.5 }}>
                  {label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar grid */}
          {weeksInMonth.map((weekMonday) => {
            const days = getWeekDays(weekMonday);
            const activeSchedules = monthLoading ? prevMonthSchedules : monthSchedules;
            const weekSchedule = activeSchedules[weekMonday];
            return (
              <Grid container spacing={0.5} key={weekMonday} sx={{ mb: 0.5 }}>
                {days.map((dateStr, idx) => {
                  const d = new Date(dateStr + 'T00:00:00');
                  const isCurrentMonth = d.getMonth() === monthNum && d.getFullYear() === monthYear;
                  const dayKey = DAY_KEYS[idx];
                  const dayData = weekSchedule?.days?.[dayKey];
                  return (
                    <Grid item xs key={dateStr}>
                      {isCurrentMonth ? (
                        <MonthCell
                          dayData={dayData}
                          dateStr={dateStr}
                          isToday={dateStr === today}
                          vacationSet={vacationSet}
                          allBranches={allBranches}
                        />
                      ) : (
                        <Box sx={{ minHeight: 64 }} />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}

          {/* Legend */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'rgba(25, 118, 210, 0.06)', border: '1px solid', borderColor: 'primary.light' }} />
              <Typography variant="caption" color="text.secondary">{t('calendar_schedule_legend_work')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }} />
              <Typography variant="caption" color="text.secondary">{t('calendar_schedule_legend_off')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'rgba(76, 175, 80, 0.08)', border: '1px solid', borderColor: 'success.light' }} />
              <Typography variant="caption" color="text.secondary">{t('calendar_schedule_legend_vacation') || 'Vacation'}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
